// app/src/lib/text.tsx — wording, with the shop's overrides applied.
//
// t("store.basket") returns the shop's word for the basket, falling back to the
// registry default. Defaults are the initial state, not a loading state, so the
// UI renders real words immediately and swaps if the shop has renamed anything.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { TEXT_DEFAULTS } from "../../../shared/ui-text";

const Ctx = createContext<{ text: Record<string, string>; reload: () => void }>({ text: {}, reload: () => {} });

export function TextProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState<Record<string, string>>({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api.get<{ text: Record<string, string> }>("/ui-text")
      .then((r) => { if (live) setText(r.text ?? {}); })
      .catch(() => { /* defaults are already correct English */ });
    return () => { live = false; };
  }, [tick]);
  const value = useMemo(() => ({ text, reload: () => setTick((n) => n + 1) }), [text]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Hook form — re-renders when the shop's wording changes. */
export function useText(): (key: string) => string {
  const { text } = useContext(Ctx);
  return (key: string) => text[key] ?? TEXT_DEFAULTS[key] ?? key;
}
export function useTextReload(): () => void { return useContext(Ctx).reload; }
